import { createContext, useContext, type ReactNode } from 'react';

/**
 * An opaque authorization key. `packages/ui` never parses it — each app owns
 * its own encoding (console: "manage:Company"; portal: "users.deactivate").
 *
 * This indirection is the whole point: the console gates on CASL abilities and
 * the portal on an Entra role claim, and neither model may enter the shared
 * library. Both implement one predicate instead.
 */
export type Capability = string;

export interface CapabilityContextValue {
  /**
   * MUST return false for any capability not in the app's registry.
   *
   * This is not automatic: the console's CASL ability grants `manage all` to
   * super_admin, so a typo'd or unregistered key would otherwise return true.
   * Each app validates against its registry BEFORE delegating.
   */
  can: (cap: Capability) => boolean;
  /** Rendered when a whole surface is denied. */
  renderDenied?: () => ReactNode;
}

export interface CapabilityProviderProps {
  value: CapabilityContextValue;
  children: ReactNode;
}

export interface CapabilityGateProps {
  cap?: Capability;
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * No provider → every DEFINED capability is denied. Undefined capabilities are
 * still allowed (see useCapability), so an ungated component renders normally
 * outside a provider.
 */
const CapabilityContext = createContext<CapabilityContextValue>({
  can: () => false,
});

/**
 * Provides capability-checking context to all descendants.
 * Passes a capability predicate down the tree so `CapabilityGate` and `useCapability` can query permissions.
 */
export function CapabilityProvider({ value, children }: CapabilityProviderProps) {
  return (
    <CapabilityContext.Provider value={value}>
      {children}
    </CapabilityContext.Provider>
  );
}

/** Access the raw context — for `renderDenied` at a page boundary. */
export function useCapabilityContext(): CapabilityContextValue {
  return useContext(CapabilityContext);
}

/**
 * Two independent axes, deliberately:
 *   cap === undefined  → true  ("no capability required")
 *   cap defined        → the injected predicate, false with no provider
 *
 * Conflating them would hide every ungated row action, since `DataColumn.cap`
 * and `RowAction.cap` are both optional.
 */
export function useCapability(cap: Capability | undefined): boolean {
  const { can } = useContext(CapabilityContext);
  if (cap === undefined) return true;
  return can(cap);
}

/**
 * Conditionally renders children based on capability check.
 *
 * Semantics:
 * - `cap: undefined` → renders children unconditionally (no capability required).
 * - `cap` defined and allowed → renders children.
 * - `cap` defined and denied with `fallback` → renders fallback.
 * - `cap` defined and denied without `fallback` → renders nothing.
 *
 * Uses `useCapability` internally, so it inherits the same two-axis check:
 * undefined capabilities always succeed; defined capabilities defer to the
 * context predicate (or default-deny if no provider).
 */
export function CapabilityGate({
  cap,
  fallback,
  children,
}: CapabilityGateProps) {
  const allowed = useCapability(cap);
  if (allowed) return <>{children}</>;
  if (fallback !== undefined) return <>{fallback}</>;
  return null;
}
